<script setup lang="ts">
import { computed } from 'vue'
import { toneOf, TONE_CLASS, type Tone } from '~/utils/state'

/**
 * Percent first (fixed width), then a 2px hairline. Finished = percent only.
 * Indeterminate machine work = the same hairline filled static in the state's tone.
 * The only motion allowed is the fill growing on mount.
 */
const props = withDefaults(defineProps<{
  percent?: number | null
  status?: string
  tone?: Tone
  barWidth?: number
  /** Bar takes the remaining width instead of a fixed one (sidebar queue). */
  grow?: boolean
}>(), { percent: null, status: '', barWidth: 56, tone: undefined })

const tone = computed(() => props.tone ?? toneOf(props.status))
const done = computed(() => props.percent !== null && props.percent >= 100)
const running = computed(() => tone.value === 'progress' || tone.value === 'waiting')
</script>

<template>
  <span class="flex min-w-0 items-center gap-2">
    <span class="num w-8 text-[length:var(--type-meta)]" :class="done ? 'text-primary' : percent === null ? 'text-muted-foreground' : 'text-foreground'">
      {{ percent === null ? '—' : percent + '%' }}
    </span>

    <span
      v-if="!done && (percent !== null || running)"
      class="h-0.5 rounded-full bg-muted"
      :class="grow && 'flex-1'"
      :style="grow ? undefined : { width: barWidth + 'px' }"
    >
      <span
        v-if="percent !== null"
        class="block h-full rounded-full bg-gradient-primary shadow-glow-bar animate-barfill"
        :style="{ width: percent + '%' }"
      />
      <span v-else class="block h-full rounded-full" :class="TONE_CLASS[tone].bar" />
    </span>
  </span>
</template>
